import React from "react";
import {
  getColorClasses,
} from "../../utils/getBackgroundColor";
import { TextBlock as TextBlockType } from "../../types/optimizely/blocks";
import classNames from "classnames";
import dompurify from "dompurify";
import { renderToString } from "react-dom/server";
import ArrowRight from "../../assets/arrow-right.svg?react";
import logo from "../../assets/wilhelmsen_logo_flag.svg";
import { isHeaderTag } from "../../utils/isHeaderTag";
import { getHeaderClasses, getHeaderClassesMap } from "../../utils/getHeaderClasses";
import { sanitizeOrPassthrough } from "../../helpers/rteHelpers";
import { SsrTagClassMap } from "../../helpers/rteSsrSanitizer";

interface TextBlockProps extends TextBlockType {}

const getAnchorClasses = (backgroundColor: string) =>
    `w-1/2 mt-7 py-3 bg-transparent ${backgroundColor} hover:${backgroundColor} items-center font-bold rounded flex`;
const localPurifier = dompurify();

const TextBlock = ({ text, backgroundColor: optiBgColor, isLogoVisible }: TextBlockProps) => {
  const backgroundColor = getColorClasses(optiBgColor?.value);

  // Mirrors the "beforeSanitizeAttributes" hook below, for SSR markup to match the client.
  const ssrTagClasses: SsrTagClassMap = {
    a: `${getAnchorClasses(backgroundColor)} font-normal group hover:text-primaryBlue`,
    ...getHeaderClassesMap(getHeaderClasses),
  };

  localPurifier.addHook("beforeSanitizeAttributes", (node) => {
    if (node.nodeName && node.nodeName === "A") {
      const hasSvgChild =
        Array.from(node.childNodes).filter((child) => child.nodeName === "SPAN")
          .length > 0;
      node.setAttribute("class", ssrTagClasses.a);

      if (!hasSvgChild) {
        const icon = renderToString(
          <span className="ml-2 group-hover:translate-x-2 transition-all ease-out">
            <ArrowRight />
          </span>
        );
        node.innerHTML += icon;
      }
    }

    if (isHeaderTag(node)) {
      node.setAttribute("class", ssrTagClasses[node.nodeName.toLowerCase()]);
    }
  });

  return (
    <div
      className={classNames(
        "p-6 lg:p-8 flex flex-col whitespace-pre-wrap",
        backgroundColor
      )}
    >
        {isLogoVisible.value && <img src={logo} alt="logo" className="object-cover" width="84px" />}
        <div
          className="text-gray-600 md:text-md w-full"
          dangerouslySetInnerHTML={{
            __html: sanitizeOrPassthrough(localPurifier, text.value, { ssrTagClassMap: ssrTagClasses }),
          }}
        />
    </div>
  );
};

export default TextBlock;
